// Modelos del módulo de Notificaciones in-app (centro de notificaciones / campana).
// Contrato del backend: GET/PUT /api/notifications*.

export type NotificationType =
  | 'TASK_ASSIGNED'
  | 'TASK_CREATED'
  | 'TASK_STATUS_CHANGED'
  | 'TASK_FOLLOWING'
  | 'TASK_COMPLETED'
  | 'TASK_OVERDUE'
  | 'TASK_OVERDUE_ESCALATION'
  | 'TASK_COMMENT'
  | 'TASK_MENTION'
  | 'TICKET_CREATED'
  | 'TICKET_RESOLVED'
  | 'TICKET_ASSIGNED'
  | 'TICKET_AUTO_ASSIGNED'
  | 'TICKET_REOPENED'
  | 'TICKET_SLA_WARNING'
  | 'TICKET_SLA_BREACHED'
  | 'TICKET_MENTION'
  | 'MEETING_SCHEDULED'
  | 'MEETING_RESCHEDULED'
  | 'MEETING_CANCELLED'
  | 'DEAL_OWNER_CHANGED'
  | 'CONTACT_OWNER_CHANGED'
  | 'LEAD_OWNER_CHANGED'
  | 'COMPANY_OWNER_CHANGED'
  | 'QUOTE_APPROVAL_REQUESTED'
  | 'QUOTE_APPROVAL_DECIDED'
  | 'QUOTE_VIEWED'
  | 'QUOTE_ACCEPTED'
  | 'QUOTE_REJECTED'
  | 'CALL_MISSED'
  | 'CONVERSATION_ASSIGNED'
  | 'CONVERSATION_TRANSFERRED'
  /** Aviso al supervisor: un visitante valoró la conversación por debajo del umbral. */
  | 'CSAT_NEGATIVE'
  /** Un correo programado llegó a su hora sin segmentos activos y volvió a borrador. */
  | 'MARKETING_CAMPAIGN_NOT_SENT'
  | 'GENERIC'
  // El front muestra cualquier `type` genéricamente (title/body), por lo que se
  // tolera cualquier string nuevo del backend sin romper el tipado.
  | (string & {});

/**
 * Entidades de negocio a las que una notificación puede apuntar.
 * Se resuelven a una ruta hash real en `NotificationService.resolveRoute`.
 */
export type NotificationContextEntityType =
  | 'TASK'
  | 'TICKET'
  | 'MEETING'
  | 'DEAL'
  | 'CONTACT'
  | 'LEAD'
  | 'COMPANY'
  | 'QUOTE'
  | 'CALL'
  | 'CONVERSATION'
  | 'MARKETING_CAMPAIGN';

/** UserNotificationResponse — una notificación del usuario logueado. */
export interface UserNotification {
  id: number;
  businessId: number;
  userId: number;
  type: NotificationType;
  title: string;
  body: string | null;
  isRead: boolean;
  readAt: string | null;
  contextEntityType: NotificationContextEntityType | null;
  contextEntityId: number | null;
  /** Ruta tentativa del backend (en español). Se normaliza en el front antes de navegar. */
  actionRoute: string | null;
  createdAt: string;
}

/**
 * Wrapper de paginación propio de este backend (NO el shape de Spring con
 * {number,first,last,empty}).
 */
export interface NotificationPageResponse {
  content: UserNotification[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

export interface UnreadCountResponse {
  count: number;
}

export interface MarkAllReadResponse {
  updated: number;
}

export interface NotificationSearchParams {
  page?: number;
  size?: number;
  unreadOnly?: boolean;
}

// ── Etiquetas legibles ─────────────────────────────────────────────────────
//
// El centro de notificaciones pinta `title`/`body` tal como los manda el backend.
// Cuando un evento nuevo llega sin título (o con la propia llave del enum como
// título) el usuario veía "TASK_ASSIGNED" en la campana. Este diccionario es la
// ÚNICA fuente de etiquetas del módulo: lo usan la campana del header y la página
// "Todas las notificaciones".

/** Texto en español de cada tipo de notificación in-app. */
export const NOTIFICATION_TYPE_LABELS: Record<string, string> = {
  TASK_ASSIGNED: 'Tarea asignada',
  TASK_CREATED: 'Tarea creada',
  TASK_STATUS_CHANGED: 'Cambio de estado de una tarea',
  TASK_FOLLOWING: 'Te agregaron como seguidor de una tarea',
  TASK_COMPLETED: 'Tarea completada',
  TASK_OVERDUE: 'Tarea vencida',
  TASK_OVERDUE_ESCALATION: 'Tarea vencida escalada',
  TASK_COMMENT: 'Nuevo comentario en una tarea',
  TASK_MENTION: 'Te mencionaron en una tarea',
  TICKET_CREATED: 'Ticket creado',
  TICKET_RESOLVED: 'Ticket resuelto',
  TICKET_ASSIGNED: 'Ticket asignado',
  TICKET_AUTO_ASSIGNED: 'Ticket asignado automáticamente',
  TICKET_REOPENED: 'Ticket reabierto',
  TICKET_SLA_WARNING: 'Aviso de SLA de un ticket',
  TICKET_SLA_BREACHED: 'SLA de un ticket incumplido',
  TICKET_MENTION: 'Te mencionaron en un ticket',
  MEETING_SCHEDULED: 'Reunión agendada',
  MEETING_RESCHEDULED: 'Reunión reprogramada',
  MEETING_CANCELLED: 'Reunión cancelada',
  DEAL_OWNER_CHANGED: 'Cambio de propietario de un negocio',
  CONTACT_OWNER_CHANGED: 'Cambio de propietario de un contacto',
  LEAD_OWNER_CHANGED: 'Cambio de propietario de un lead',
  COMPANY_OWNER_CHANGED: 'Cambio de propietario de una empresa',
  QUOTE_APPROVAL_REQUESTED: 'Aprobación de cotización solicitada',
  QUOTE_APPROVAL_DECIDED: 'Aprobación de cotización resuelta',
  QUOTE_VIEWED: 'Cotización vista por el cliente',
  QUOTE_ACCEPTED: 'Cotización aceptada',
  QUOTE_REJECTED: 'Cotización rechazada',
  CALL_MISSED: 'Llamada perdida',
  CONVERSATION_ASSIGNED: 'Conversación asignada',
  CONVERSATION_TRANSFERRED: 'Conversación transferida',
  CSAT_NEGATIVE: 'Valoración negativa de un chat',
  MARKETING_CAMPAIGN_NOT_SENT: 'Correo programado no enviado',
  GENERIC: 'Notificación'
};

/** Nombre en español de la entidad a la que apunta la notificación. */
export const NOTIFICATION_ENTITY_LABELS: Record<string, string> = {
  TASK: 'Tarea',
  TICKET: 'Ticket',
  MEETING: 'Reunión',
  DEAL: 'Negocio',
  CONTACT: 'Contacto',
  LEAD: 'Lead',
  COMPANY: 'Empresa',
  QUOTE: 'Cotización',
  CALL: 'Llamada',
  CONVERSATION: 'Conversación',
  MARKETING_CAMPAIGN: 'Correo de marketing'
};

/** ¿El texto es la llave cruda de un enum (`TASK_ASSIGNED`) en vez de una frase? */
function esLlaveDeEnum(texto: string): boolean {
  return /^[A-Z][A-Z0-9]*(?:_[A-Z0-9]+)+$/.test(texto.trim());
}

/**
 * Título legible de una notificación: el del backend si es una frase; si viene
 * vacío o con la llave del enum, la etiqueta en español del tipo.
 */
export function notificationTitle(n: UserNotification): string {
  const titulo = (n.title ?? '').trim();
  if (titulo && !esLlaveDeEnum(titulo)) return titulo;
  return NOTIFICATION_TYPE_LABELS[n.type] ?? NOTIFICATION_TYPE_LABELS['GENERIC'];
}

/**
 * Cuerpo legible de una notificación: `null` cuando no hay nada que mostrar o
 * cuando lo único que llegó fue una llave de enum / un id suelto.
 */
export function notificationBody(n: UserNotification): string | null {
  const cuerpo = (n.body ?? '').trim();
  if (!cuerpo) return null;
  if (esLlaveDeEnum(cuerpo)) return null;
  if (/^#?\d+$/.test(cuerpo)) return null;
  return cuerpo;
}
